import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import MetaFormInline from 'Components/UI/Meta/MetaForm/Components/MetaFormInline';
import TableCellLayout
  from 'Views/Page/Content/Templates/WhatIfTemplate/Content/RightSide/BottomPart/DetailPicker/Details/Content/TabTemplatePicker/Allocations/TableView/CollapsableTable/TableCellLayout';
import AllocationTableCellFormOverride from 'Overrides/WhatIf/AllocationTableCellFormOverride';
import { currency, percent } from 'Helpers/Formats';
import {
  RESOURCE_TYPES,
} from 'Views/Page/Content/Templates/WhatIfTemplate/Content/RightSide/BottomPart/DetailPicker/Details/Content/TabTemplatePicker/Allocations/TableView';
import Popover from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Popover';
import usePopoverState from 'Hooks/usePopoverState';
import Tooltip from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Tooltip';
import TableCellContent from './TableCellContent';

const styles = (theme) => ({
  root: {
    padding: theme.spacing(1),
    textAlign: 'right',
  },
  title: {
    flexBasis: '30%',
    flexShrink: 0,
  },
  cellMetaForm: {
    padding: 0,
  },
  highlightedCellPrimary: {
    backgroundColor: theme.palette.secondary.main,
    color: 'white',
  },
  borderRadiusLeft: {
    borderTopLeftRadius: theme.radius.xl,
    borderBottomLeftRadius: theme.radius.xl,
  },
  borderRadiusRight: {
    borderTopRightRadius: theme.radius.xl,
    borderBottomRightRadius: theme.radius.xl,
  },
  allocationModelCell: {
    whiteSpace: 'normal',
  },
  plainButton: {
    display: 'block',
    width: '100%',
    padding: '0',
    backgroundColor: 'transparent',
    border: 'none',
    font: 'inherit',
    textAlign: 'inherit',
    color: theme.palette.text.primary,
  },
  largerText: {
    fontSize: theme.typography.fontSizePt875Rem,
  },
});

export const getStyleFromCellType = (cellType) => {
  switch (cellType) {
    case 'topLevel':
      return {
        color: 'primary',
      };
    case 'allocationModel':
    case 'rateOfReturn':
    case 'midLevel':
      return {
        color: 'primaryGray',
        className: 'midLevel',
      };
    case 'bottomLevel':
      return {
        color: 'primaryGray',
        className: 'bottomLevel',
      };
    case 'totalLevel':
      return {
        color: 'black',
        className: 'totalLevel',
      };
    case 'subtotalLevel':
      return {
        color: 'black',
        className: 'subtotalLevel',
      };
    default:
      return {};
  }
};

const formatCellContentByType = (cellType, cellValue) => {
  switch (cellType) {
    case 'allocationModel':
      return cellValue;
    case 'rateOfReturn':
      return percent(Math.floor(10000 * cellValue) / 100);
    default:
      return currency(cellValue, true);
  }
};

const TableRowCell = ({
  data,
  className,
  classes,
  highlightedByType,
  cellType,
  test,
  onCellFormSubmitOverride,
}) => {
  let color;
  if (data.cellResource.attr('warningMessage') || data.cellResource.isTruthy('userModified')) {
    color = 'orange';
  }
  if (data.cellResource.attr('hasInfoAlert') === 'true') {
    color = 'primary';
  }
  let cellContent = formatCellContentByType(cellType, data.value);
  if (data.cellResource.type() === 'resourceTableDataCellPercentage') {
    cellContent = percent(data.percentValue);
  }

  const attrName = data.cellResource.type() === 'resourceTableDataCellPercentage' ? 'percentValue' : 'value';
  const initialCellValue = useMemo(() => data.cellResource.attr(attrName), [data.cellResource]);
  if (data.cellResource.attr(attrName) === '') {
    cellContent = '-';
  }

  const hasAllocationsBefore = data.cellResource.attr('hasAllocationsBefore') === 'true';
  const hasAllocationsAfter = data.cellResource.attr('hasAllocationsAfter') === 'true';
  const primaryHighlight = highlightedByType[RESOURCE_TYPES.highlightedPrimary];

  const {
    open,
    anchorEl,
    handleClose,
    openPopover,
  } = usePopoverState({});
  const valueFieldMeta = data.cellResource.meta().field(attrName);

  return (
    <td className={classNames(classes.root, className, {
      [classes.highlightedCellPrimary]: primaryHighlight?.matches,
      [classes.borderRadiusLeft]: primaryHighlight?.matches && !hasAllocationsBefore,
      [classes.borderRadiusRight]: primaryHighlight?.matches && !hasAllocationsAfter,
    })}
    >
      <TableCellContent>
        {(data.cellResource.hasOwnMeta || data.cellResource.link('self')) && !valueFieldMeta.attr('readonly') ? (
          <>
            <Tooltip title={data.cellResource.attr('altValue') ? data.cellResource.attr('altValue') : data.cellResource.attr('warningMessage')} placement="top">
              <button className={classes.plainButton} onClick={openPopover} type="button">
                <Typography
                  className={classes.largerText}
                  noWrap
                  color={color}
                  weight={'bold'}
                  test={test}
                  underline={cellContent !== '-'}
                >
                  {cellContent}
                </Typography>
              </button>
            </Tooltip>
            {open && (
            <Popover
              open
              anchorEl={anchorEl}
              onClose={(event) => {
                data.cellResource.attr(attrName, initialCellValue);
                event.stopPropagation();
                handleClose();
              }}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              test={`scenario-allocation-popover-${open}`}
              rounded
            >
              <MetaFormInline
                className={classes.cellMetaForm}
                resource={data.cellResource}
                layout={TableCellLayout}
                metaOverrideSet={AllocationTableCellFormOverride}
                readonly
                test={test}
                LayoutProps={{
                  hideErrorList: true, // hide error list from each cell
                  handleClose,
                  attrName,
                  onCellFormSubmitOverride,
                  rowResource: data.rowResource,
                }}
              />
            </Popover>
            )}
          </>
        ) : (
          <Typography
            className={classNames({
              [classes.allocationModelCell]: cellType === 'allocationModel',
            }, classes.largerText)}
            noWrap
            title={cellContent}
            weight={'bold'}
            test={test}
          >
            {cellContent}
          </Typography>
        )}
      </TableCellContent>
    </td>
  );
};

TableRowCell.defaultProps = {
  className: '',
  data: null,
  test: 'table-row-cell',
  cellType: null,
  onCellFormSubmitOverride: null,
};

TableRowCell.propTypes = {
  data: PropTypes.object,
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  highlightedByType: PropTypes.object.isRequired,
  test: PropTypes.string,
  cellType: PropTypes.string,
  onCellFormSubmitOverride: PropTypes.func,
};

export default withStyles(styles)(TableRowCell);
